import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import api from "./../../axiosInstance";
import Loader from '../GlobalLoader/Loader';
import logger from "../../utils/logger";

// ─── Constants ────────────────────────────────────────────────────────────────
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];


const INR = (n) => "₹" + Math.round(n).toLocaleString("en-IN");
const INRd = (n) => "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Calendar ─────────────────────────────────────────────────────────────────
function Calendar({ year, month, selectedDate, onSelect, onPrevMonth, onNextMonth }) {
  const today = new Date();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMo = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const trailing = (7 - ((firstDay + daysInMo) % 7)) % 7;

  const cells = [];
  for (let i = 0; i < firstDay; i++)
    cells.push({ day: prevDays - firstDay + 1 + i, type: "prev" });
  for (let d = 1; d <= daysInMo; d++)
    cells.push({ day: d, type: "cur" });
  for (let i = 1; i <= trailing; i++)
    cells.push({ day: i, type: "next" });

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isSelected = (d) => selectedDate?.d === d && selectedDate?.m === month && selectedDate?.y === year;
  const isPast = (d) => new Date(year, month, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div style={S.calBox}>
      <div style={S.calNav}>
        <button onClick={onPrevMonth} style={S.calNavBtn}>‹</button>
        <span style={{ fontSize: 12, fontWeight: 500, color: "#1a2a3a" }}>{MONTHS[month]} {year}</span>
        <button onClick={onNextMonth} style={S.calNavBtn}>›</button>
      </div>
      <div style={S.calGrid}>
        {DAYS.map(d => <div key={d} style={S.calDayHdr}>{d}</div>)}
        {cells.map((c, i) => {
          if (c.type !== "cur") return <div key={i} style={S.calDayOther}>{c.day}</div>;
          const past = isPast(c.day) && !isToday(c.day);
          const sel = isSelected(c.day);
          const tod = isToday(c.day);
          return (
            <div
              key={i}
              onClick={() => !past && onSelect(c.day)}
              style={{
                ...S.calDay,
                ...(past && !tod ? S.calDayDisabled : {}),
                ...(tod && !sel ? S.calDayToday : {}),
                ...(sel ? S.calDaySelected : {}),
              }}
            >
              {c.day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TimeSlots ────────────────────────────────────────────────────────────────
function TimeSlots({ slots, selectedSlot, onSelect, loading }) {
  const fmt = (t) => `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;

  return (
    <div style={{ ...S.slotsBox, minHeight: 140 }}>
      {loading ? (
        <div style={S.slotMsg}>Loading slots…</div>
      ) : !slots || slots.length === 0 ? (
        <div style={S.slotMsg}>Select a date to view available slots</div>
      ) : (
        <div style={S.slotsGrid}>
          {slots.map((slot, i) => (
            <div
              key={i}
              onClick={() => onSelect(i)}
              style={{ ...S.slot, ...(selectedSlot === i ? S.slotSel : {}) }}
            >
              {`${fmt(slot.startTime)} – ${fmt(slot.endTime)}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SkuRow ───────────────────────────────────────────────────────────────────
function SkuRow({ item, index, onQtyChange, onDiscChange, onRemove }) {
  const gross = item.unitPrice * item.qty;
  const disc = gross * (item.lineDisc || 0) / 100;
  const after = gross - disc;
  const taxRate = (item.cgst || 0) + (item.sgst || 0) + (item.igst || 0);
  const total = after + after * taxRate / 100;

  return (
    <div style={S.skuRow}>
      <div style={{ padding: "8px 10px" }}>
        <div style={S.skuName}>{item.name}</div>
        <div style={S.skuCat}>{item.category}</div>
      </div>
      <div style={S.skuCell}>{INR(item.unitPrice)}</div>
      <div style={S.skuCell}>
        <input type="number" min="1" max="99" value={item.qty}
          onChange={e => onQtyChange(index, e.target.value)}
          style={{ ...S.numInp, width: 50 }} />
      </div>
      <div style={{ ...S.skuCell, display: "flex", alignItems: "center", gap: 3 }}>
        <input type="number" min="0" max="100" value={item.lineDisc}
          onChange={e => onDiscChange(index, e.target.value)}
          style={{ ...S.numInp, width: 46 }} />
        <span style={{ fontSize: 11, color: "#9ca3af" }}>%</span>
      </div>
      <div style={{ ...S.skuCell, fontWeight: 500 }}>{INR(total)}</div>
      <div style={{ padding: "0 4px" }}>
        <button onClick={() => onRemove(index)} style={S.delBtn}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} style={{ ...S.togTrack, background: on ? "#1d9e75" : "#d1d5db" }}>
      <div style={{ ...S.togThumb, left: on ? 19 : 3 }} />
    </button>
  );
}

// ─── StepIndicator ────────────────────────────────────────────────────────────
function StepIndicator({ step }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {["Appointment", "Invoice"].map((label, i) => {
        const idx = i + 1;
        const done = idx < step;
        const active = idx === step;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            {i > 0 && <div style={{ width: 20, height: 1, background: "#374151" }} />}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 10, fontWeight: 600,
                background: done ? "#1d9e75" : active ? "#f5c842" : "#374151",
                color: done ? "#fff" : active ? "#1a2a3a" : "#8899aa",
              }}>
                {done ? "✓" : idx}
              </div>
              <span style={{ fontSize: 11, color: (done || active) ? "#fff" : "#6b7280" }}>{label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const AppointmentSelectSlotPage = ({ onClose, patient, selectedProfile, setIsAppointmentSelectDatePopupOpen }) => {
  const { user } = useAuth();
  const { selectedClinic, injectNewAppointment, setBoardDate, fetchClinicData } = useApp();

  const today = new Date();

  // Step & date/slot state
  const [step, setStep] = useState(1);
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [apptTypes, setApptTypes] = useState([]);
  const [apptType, setApptType] = useState("");
  const [apptNotes, setApptNotes] = useState("");
  const [step1Err, setStep1Err] = useState("");

  // Invoice state
  const [skuCatalog, setSkuCatalog] = useState([]);
  const [skuItems, setSkuItems] = useState([]);
  const [skuPickerId, setSkuPickerId] = useState("");
  const [insEnabled, setInsEnabled] = useState(false);
  const [insCompany, setInsCompany] = useState("");
  const [insPolicy, setInsPolicy] = useState("");
  const [insCoverage, setInsCoverage] = useState(0);
  const [payMethod, setPayMethod] = useState("Cash");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [confirmedGrandTotal, setConfirmedGrandTotal] = useState(null);

  const patientName = patient?.name?.firstName
    ? `${patient.name.firstName} ${patient.name.lastName}`
    : selectedProfile?.name?.firstName
      ? `${selectedProfile.name.firstName} ${selectedProfile.name.lastName}`
      : "Patient";

  const patientId = patient?._id || selectedProfile?.id || null;

  // Fetch clinic SKUs + appointment types on mount
  useEffect(() => {
    const clinicId = selectedClinic?._id;
    if (!clinicId) return;
    Promise.all([
      api.get("/clinicSKU", { params: { clinicId, limit: 1000, page: 1 } }),
      api.get("/appointmentTypes", { params: { clinicId, isActive: true, limit: 100, page: 1 } }),
    ]).then(([skuRes, typesRes]) => {
      const raw = skuRes.data.data || [];
      const catalog = raw
        .filter(s => s.isActive !== false)
        .map(s => ({
          _id: s._id,
          clinicSkuId: s.clinicSkuId,
          name: s.name,
          category: s.category || "",
          unitPrice: s.unitPrice,
          cgst: s.tax?.cgst ?? 0,
          sgst: s.tax?.sgst ?? 0,
          igst: s.tax?.igst ?? 0,
        }));
      setSkuCatalog(catalog);

      const types = typesRes.data.data || [];
      setApptTypes(types);
      if (types.length > 0) {
        setApptType(types[0]._id);
        applyDefaultSkus(types[0], catalog);
      }
    }).catch(err => logger.error("Error fetching data:", err));
  }, [selectedClinic]);

  const applyDefaultSkus = (type, catalog) => {
    if (!type?.defaultSkus?.length) return;
    const items = type.defaultSkus.map(ds => {
      // skuId may be a raw ObjectId string OR a populated object { _id, name, ... }
      const skuId = String(ds.skuId?._id || ds.skuId || "");
      const sku = catalog.find(s => String(s._id) === skuId);
      if (!sku) return null;
      return { ...sku, qty: ds.qty || 1, lineDisc: ds.discount || 0 };
    }).filter(Boolean);
    setSkuItems(items);
  };

  const handleApptTypeChange = (typeId) => {
    setApptType(typeId);
    const type = apptTypes.find(t => t._id === typeId);
    applyDefaultSkus(type, skuCatalog);
  };

  // Calendar navigation
  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  // Fetch empty slots when a date is selected
  const handleDateSelect = async (day) => {
    setSelectedDate({ d: day, m: calMonth, y: calYear });
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const dateString = new Date(Date.UTC(calYear, calMonth, day)).toISOString().slice(0, 10);
      logger.debug("findEmptySlots",dateString,day)
      const res = await api.get(`/clinics/findEmptySlots/${selectedClinic._id}`, {
        params: { doctorId: user._id, date: dateString },
      });
      setTimeSlots(res.data.emptySlots || []);
    } catch (err) {
      logger.error("Error fetching slots:", err);
      setTimeSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Totals (server will recompute on invoice creation, this is just for display)
  const totals = useMemo(() => {
    let subTotal = 0, lineDiscTotal = 0, taxTotal = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
    for (const s of skuItems) {
      const gross = s.unitPrice * s.qty;
      const disc = gross * (s.lineDisc || 0) / 100;
      const after = gross - disc;
      subTotal += gross;
      lineDiscTotal += disc;
      cgstTotal += after * (s.cgst || 0) / 100;
      sgstTotal += after * (s.sgst || 0) / 100;
      igstTotal += after * (s.igst || 0) / 100;
      taxTotal += after * ((s.cgst || 0) + (s.sgst || 0) + (s.igst || 0)) / 100;
    }
    const afterLineDsc = subTotal - lineDiscTotal;
    const insAmt = insEnabled ? afterLineDsc * insCoverage / 100 : 0;
    const grand = afterLineDsc - insAmt + taxTotal;
    return { subTotal, lineDiscTotal, insAmt, taxTotal, cgstTotal, sgstTotal, igstTotal, grand };
  }, [skuItems, insEnabled, insCoverage]);

  // SKU actions
  const addSku = () => {
    if (!skuPickerId) return;
    const sku = skuCatalog.find(s => s.clinicSkuId === skuPickerId);
    if (!sku || skuItems.find(s => s.clinicSkuId === sku.clinicSkuId)) { setSkuPickerId(""); return; }
    setSkuItems(prev => [...prev, { ...sku, qty: 1, lineDisc: 0 }]);
    setSkuPickerId("");
  };
  const removeSku = (i) => setSkuItems(prev => prev.filter((_, idx) => idx !== i));
  const updateQty = (i, v) => setSkuItems(prev => prev.map((s, idx) => idx === i ? { ...s, qty: Math.max(1, parseInt(v) || 1) } : s));
  const updateLineDsc = (i, v) => setSkuItems(prev => prev.map((s, idx) => idx === i ? { ...s, lineDisc: Math.min(100, Math.max(0, parseFloat(v) || 0)) } : s));

  // Step 1 → Step 2
  const goStep2 = () => {
    if (!selectedDate) { setStep1Err("Please select a date"); return; }
    if (selectedSlot === null) { setStep1Err("Please select a time slot"); return; }
    setStep1Err("");
    setStep(2);
  };

  // Book appointment + create invoice
  const confirmBooking = async () => {
    setLoadingSubmit(true);
    try {
      // 1. Book the appointment
      const { d, m, y } = selectedDate;
      const slot = timeSlots[selectedSlot];
      const { hour, minute } = slot.startTime;
      const dateOfAppt = new Date(y, m, d, hour, minute);

      const apptBody = {
        clinicId: selectedClinic._id,
        doctorId: user._id,
        patientId,
        doctorName: `${user.name.firstName} ${user.name.lastName}`,
        patientName,
        clinicName: selectedClinic.name.fullName,
        appointmentName: apptTypes.find(t => t._id === apptType)?.name || apptType,
        dateOfAppointment: dateOfAppt.toISOString(),
        dateOfBooking: new Date().toISOString(),
        tags: apptTypes.find(t => t._id === apptType)?.name || apptType
      };

      const apptRes = await api.post('/appointments', apptBody);
      let newAppointment
      newAppointment = apptRes.data.appointment;
      // console.log(newAppointment)


      // 2. Always create invoice (with or without SKUs)
      {
        const dueDate = new Date(Date.UTC(y, m, d + 30)).toISOString().slice(0, 10);
        const invBody = {
          clinicId: selectedClinic._id,
          patientId,
          doctorId: user._id,
          appointments: [newAppointment._id],
          dueDate,
          ...(insEnabled ? {
            insuranceDetails: {
              insuranceCompany: insCompany,
              policyNumber: insPolicy,
              coveragePercent: insCoverage,
            },
          } : {}),
          expenseTable: skuItems.map(s => ({
            skuId: s.clinicSkuId,
            quantity: s.qty,
            discount: s.lineDisc || 0,
          })),
        };
        logger.debug("Create invBody", invBody);
        const invResp = await api.post('/medicalExpenditures', invBody);

        const medicalExpenditureId = invResp.data._id;
        const apptResp2 = await api.put(`/appointments/${newAppointment._id}`, { medicalExpenditureId });
        newAppointment = apptResp2.data;
        logger.debug('medicalExpenditureId', medicalExpenditureId);
      }
      injectNewAppointment(newAppointment);
      setBoardDate(newAppointment.dateOfAppointment.split("T")[0]);
      setConfirmedGrandTotal(totals.grand);

      if (user?.clinics) fetchClinicData(user.clinics.join(","));
      setStep(3);
    } catch (err) {
      logger.error("Error booking appointment:", err);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleClose = () => {
    setIsAppointmentSelectDatePopupOpen(false);
    onClose?.();
  };

  const fmtSlot = (slot) => {
    if (!slot) return "";
    const f = (t) => `${String(t.hour).padStart(2, "0")}:${String(t.minute).padStart(2, "0")}`;
    return `${f(slot.startTime)} – ${f(slot.endTime)}`;
  };

  const modalSubtitle = step === 1
    ? "Step 1 of 2 · Select date, time & appointment details"
    : step === 2
      ? `Step 2 of 2 · ${selectedDate ? `${MONTHS[selectedDate.m]} ${selectedDate.d}, ${selectedDate.y} · ${fmtSlot(timeSlots[selectedSlot])}` : ""} · ${apptTypes.find(t => t._id === apptType)?.name || ""}`
      : "Appointment confirmed";

  return (
    <>
      {loadingSubmit && (
        <div style={S.loaderOverlay}>
          <Loader message="Please wait, booking appointment…" />
        </div>
      )}

      <div style={S.backdrop} onClick={e => e.target === e.currentTarget && handleClose()}>
        <div style={S.modal}>

          {/* ── Header ── */}
          <div style={S.mhead}>
            <div>
              <div style={S.mheadTitle}>
                {step < 3 ? `Book new appointment – ${patientName}` : "Appointment confirmed"}
              </div>
              <div style={S.mheadSub}>{modalSubtitle}</div>
            </div>
            {step < 3
              ? <StepIndicator step={step} />
              : (
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1d9e75", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
              )
            }
          </div>

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              <div style={S.mbody}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
                  <div>
                    <div style={S.sectionTitle}>Select date</div>
                    <Calendar
                      year={calYear} month={calMonth} selectedDate={selectedDate}
                      onSelect={handleDateSelect}
                      onPrevMonth={prevMonth} onNextMonth={nextMonth}
                    />
                  </div>
                  <div>
                    <div style={S.sectionTitle}>Select time slot</div>
                    <TimeSlots
                      slots={timeSlots} selectedSlot={selectedSlot}
                      onSelect={setSelectedSlot} loading={loadingSlots}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div style={S.formGroup}>
                    <label style={S.flbl}>Appointment type</label>
                    <select value={apptType} onChange={e => handleApptTypeChange(e.target.value)} style={S.fsel}>
                      <option value="">— Select type —</option>
                      {apptTypes.map(t => (
                        <option key={t._id} value={t._id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ ...S.formGroup, gridColumn: "1/-1" }}>
                    <label style={S.flbl}>Notes (optional)</label>
                    <input value={apptNotes} onChange={e => setApptNotes(e.target.value)}
                      placeholder="Any notes for this appointment…" style={S.finp} />
                  </div>
                </div>
              </div>

              <div style={S.mfoot}>
                <button onClick={handleClose} style={S.btnCancelRed}>Cancel</button>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {step1Err && <span style={{ fontSize: 12, color: "#b45309" }}>{step1Err}</span>}
                  <button onClick={goStep2} style={S.btnNext}>Continue to invoice →</button>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <>
              <div style={S.mbody}>
                {/* SKU table */}
                <div style={S.sectionTitle}>Services &amp; items</div>
                <div style={S.invSkus}>
                  <div style={S.invHdr}>
                    {["Service / item", "Unit price", "Qty", "Discount %", "Total", ""].map((h, i) => (
                      <span key={i} style={S.invHdrCell}>{h}</span>
                    ))}
                  </div>
                  {skuItems.length === 0
                    ? <div style={{ padding: 16, textAlign: "center", fontSize: 12, color: "#9ca3af" }}>No items added yet</div>
                    : skuItems.map((s, i) => (
                      <SkuRow key={s.clinicSkuId} item={s} index={i}
                        onQtyChange={updateQty} onDiscChange={updateLineDsc} onRemove={removeSku}
                      />
                    ))
                  }
                  <div style={S.addSkuRow}>
                    <select value={skuPickerId} onChange={e => setSkuPickerId(e.target.value)} style={S.addSkuSel}>
                      <option value="">— Add a service or product —</option>
                      {skuCatalog
                        .filter(s => !skuItems.find(x => x.clinicSkuId === s.clinicSkuId))
                        .map(s => (
                          <option key={s.clinicSkuId} value={s.clinicSkuId}>
                            {s.name} – {INR(s.unitPrice)}
                          </option>
                        ))
                      }
                    </select>
                    <button onClick={addSku} style={S.btnAddSku}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0f6e56" strokeWidth="2.2" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Add item
                    </button>
                  </div>
                </div>

                {/* Insurance + Payment */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                  <div style={S.discBox}>
                    <div style={S.discBoxTitle}>Insurance</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: "#374151" }}>Apply insurance</span>
                      <Toggle on={insEnabled} onToggle={() => setInsEnabled(v => !v)} />
                    </div>
                    {insEnabled && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <input value={insCompany} onChange={e => setInsCompany(e.target.value)}
                          placeholder="Insurance company" style={{ ...S.finp, height: 30, fontSize: 12 }} />
                        <input value={insPolicy} onChange={e => setInsPolicy(e.target.value)}
                          placeholder="Policy number" style={{ ...S.finp, height: 30, fontSize: 12 }} />
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <input type="number" min="0" max="100" value={insCoverage}
                            onChange={e => setInsCoverage(parseFloat(e.target.value) || 0)}
                            style={{ ...S.discInp, flex: 1 }} placeholder="0" />
                          <span style={{ fontSize: 12, color: "#6b7280" }}>% covered</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={S.discBoxTitle}>Payment method</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                      {["Cash", "UPI", "Card", "Insurance"].map(m => (
                        <button key={m} onClick={() => setPayMethod(m)}
                          style={{ ...S.modeBtn, ...(payMethod === m ? S.modeBtnOn : {}) }}>
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Totals */}
                <div style={S.totalsBox}>
                  {[
                    ["Subtotal", INR(totals.subTotal), null],
                    ["Line discounts", "−" + INR(totals.lineDiscTotal), "#16a34a"],
                    ["Insurance coverage", "−" + INR(totals.insAmt), "#16a34a"],
                  ].map(([label, val, col]) => (
                    <div key={label} style={S.totRow}>
                      <span>{label}</span>
                      <span style={col ? { color: col } : {}}>{val}</span>
                    </div>
                  ))}
                  {totals.cgstTotal > 0 && <div style={S.totRow}><span>CGST</span><span>+{INR(totals.cgstTotal)}</span></div>}
                  {totals.sgstTotal > 0 && <div style={S.totRow}><span>SGST</span><span>+{INR(totals.sgstTotal)}</span></div>}
                  {totals.igstTotal > 0 && <div style={S.totRow}><span>IGST</span><span>+{INR(totals.igstTotal)}</span></div>}
                  {totals.taxTotal > 0 && (
                    <div style={{ ...S.totRow, fontStyle: "italic", color: "#6b7280" }}>
                      <span>Total tax</span><span>+{INR(totals.taxTotal)}</span>
                    </div>
                  )}
                  <div style={{ ...S.totRow, ...S.totRowGrand }}>
                    <span>Grand total</span>
                    <span>{INRd(totals.grand)}</span>
                  </div>
                </div>
              </div>

              <div style={S.mfoot}>
                <button onClick={() => setStep(1)} style={S.btnBack}>← Back</button>
                <button onClick={confirmBooking} style={S.btnConfirm}>Confirm &amp; book appointment</button>
              </div>
            </>
          )}

          {/* ── STEP 3 – Confirmation ── */}
          {step === 3 && (
            <div style={S.mbody}>
              <div style={S.summaryBox}>
                {[
                  ["Patient", patientName],
                  ["Date & time", selectedDate ? `${MONTHS[selectedDate.m]} ${selectedDate.d}, ${selectedDate.y} · ${fmtSlot(timeSlots[selectedSlot])}` : ""],
                  ["Appointment type", apptType],
                  ["Services", skuItems.length > 0 ? skuItems.map(s => s.name).join(", ") : "—"],
                  ["Payment method", payMethod],
                ].map(([lbl, val]) => (
                  <div key={lbl} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151", padding: "4px 0" }}>
                    <span style={{ color: "#6b7280" }}>{lbl}</span>
                    <span>{val}</span>
                  </div>
                ))}
                {confirmedGrandTotal != null && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 600, color: "#1a2a3a", padding: "10px 0 2px", borderTop: "0.5px solid #e8eaed", marginTop: 6 }}>
                    <span>Grand total</span>
                    <span style={{ color: "#1d9e75" }}>{INRd(confirmedGrandTotal)}</span>
                  </div>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <button onClick={handleClose} style={S.btnBack}>Close</button>
                <button onClick={handleClose} style={S.btnConfirm}>Done</button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default AppointmentSelectSlotPage;

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  loaderOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" },
  backdrop: { position: "fixed", inset: 0, background: "rgba(26,42,58,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'DM Sans',system-ui,sans-serif" },
  modal: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, overflow: "hidden", maxHeight: "92vh", overflowY: "auto" },
  mhead: { background: "#023047", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  mheadTitle: { color: "#fff", fontSize: 15, fontWeight: 600 },
  mheadSub: { color: "#8899aa", fontSize: 11, marginTop: 2 },
  mbody: { padding: "20px 22px" },
  mfoot: { padding: "14px 22px", borderTop: "0.5px solid #e8eaed", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 },
  sectionTitle: { fontSize: 11, fontWeight: 500, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 10 },

  calBox: { border: "0.5px solid #e8eaed", borderRadius: 10, overflow: "hidden" },
  calNav: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f9fafb", borderBottom: "0.5px solid #e8eaed" },
  calNavBtn: { width: 24, height: 24, border: "0.5px solid #e8eaed", borderRadius: 5, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#374151", fontFamily: "inherit" },
  calGrid: { display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1, padding: 8 },
  calDayHdr: { fontSize: 10, color: "#9ca3af", textAlign: "center", padding: "3px 0", fontWeight: 500 },
  calDay: { fontSize: 12, textAlign: "center", padding: "5px 3px", borderRadius: 6, cursor: "pointer", color: "#374151" },
  calDayOther: { fontSize: 12, textAlign: "center", padding: "5px 3px", color: "#d1d5db" },
  calDayToday: { background: "#f5c842", color: "#1a2a3a", fontWeight: 600 },
  calDaySelected: { background: "#1d9e75", color: "#fff", fontWeight: 600 },
  calDayDisabled: { color: "#d1d5db", cursor: "default" },

  slotsBox: { border: "0.5px solid #e8eaed", borderRadius: 10, padding: 12 },
  slotsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 },
  slotMsg: { textAlign: "center", padding: "24px 0", fontSize: 12, color: "#9ca3af" },
  slot: { padding: "7px 10px", border: "0.5px solid #e8eaed", borderRadius: 7, fontSize: 12, color: "#374151", cursor: "pointer", textAlign: "center" },
  slotSel: { background: "#1d9e75", color: "#fff", borderColor: "#1d9e75", fontWeight: 500 },

  formGroup: { display: "flex", flexDirection: "column", gap: 4 },
  flbl: { fontSize: 11, fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: ".04em" },
  finp: { height: 34, border: "0.5px solid #e8eaed", borderRadius: 7, padding: "0 10px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" },
  fsel: { height: 34, border: "0.5px solid #e8eaed", borderRadius: 7, padding: "0 10px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", width: "100%" },

  invSkus: { border: "0.5px solid #e8eaed", borderRadius: 10, overflow: "hidden", marginBottom: 14 },
  invHdr: { display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 0.8fr 28px", background: "#f9fafb", borderBottom: "0.5px solid #e8eaed" },
  invHdrCell: { fontSize: 10, color: "#6b7280", fontWeight: 500, textTransform: "uppercase", letterSpacing: ".04em", padding: "8px 10px" },
  skuRow: { display: "grid", gridTemplateColumns: "2fr 1fr 0.8fr 0.8fr 0.8fr 28px", borderBottom: "0.5px solid #f5f5f5", alignItems: "center" },
  skuCell: { padding: "8px 10px", fontSize: 12, color: "#1a2a3a" },
  skuName: { fontSize: 12, fontWeight: 500, color: "#1a2a3a" },
  skuCat: { fontSize: 10, color: "#9ca3af" },
  numInp: { height: 28, border: "0.5px solid #e8eaed", borderRadius: 5, padding: "0 6px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit", textAlign: "center" },
  delBtn: { width: 22, height: 22, border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 },
  addSkuRow: { padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, borderTop: "0.5px solid #e8eaed", background: "#fafafa" },
  addSkuSel: { flex: 1, height: 30, border: "0.5px solid #e8eaed", borderRadius: 6, padding: "0 8px", fontSize: 12, color: "#374151", background: "#fff", outline: "none", fontFamily: "inherit" },
  btnAddSku: { height: 30, padding: "0 12px", border: "0.5px solid #1d9e75", borderRadius: 6, background: "#e1f5ee", color: "#0f6e56", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" },

  discBox: { border: "0.5px solid #e8eaed", borderRadius: 10, padding: 12 },
  discBoxTitle: { fontSize: 11, fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 },
  modeBtn: { flex: 1, height: 28, border: "0.5px solid #e8eaed", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "inherit", background: "#fff", color: "#6b7280" },
  modeBtnOn: { background: "#1a2a3a", color: "#fff", borderColor: "#1a2a3a" },
  discInp: { height: 30, border: "0.5px solid #e8eaed", borderRadius: 6, padding: "0 8px", fontSize: 12, color: "#1a2a3a", background: "#f9fafb", outline: "none", fontFamily: "inherit" },

  togTrack: { width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer", position: "relative", transition: "background .2s" },
  togThumb: { position: "absolute", top: 3, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left .2s" },

  totalsBox: { background: "#f9fafb", border: "0.5px solid #e8eaed", borderRadius: 10, padding: "12px 14px" },
  totRow: { display: "flex", justifyContent: "space-between", fontSize: 12, color: "#374151", padding: "3px 0" },
  totRowGrand: { borderTop: "0.5px solid #e8eaed", marginTop: 6, paddingTop: 8, fontSize: 14, fontWeight: 600, color: "#1a2a3a" },

  summaryBox: { background: "#f9fafb", border: "0.5px solid #e8eaed", borderRadius: 10, padding: 14, marginBottom: 14 },

  btnBack: { height: 36, padding: "0 18px", border: "0.5px solid #e8eaed", borderRadius: 8, background: "#fff", fontSize: 13, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" },
  btnNext: { height: 36, padding: "0 22px", border: "none", borderRadius: 8, background: "#1d9e75", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  btnCancelRed: { height: 36, padding: "0 18px", border: "none", borderRadius: 8, background: "#fee2e2", color: "#b91c1c", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  btnConfirm: { height: 36, padding: "0 22px", border: "none", borderRadius: 8, background: "#1a2a3a", color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
};
